import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Navigation, 
  X, 
  ArrowRight, 
  Activity, 
  Truck, 
  Bike
} from 'lucide-react';

const PriceCalculator = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('input');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');

  // Simulated rates per KM
  const providers = [
    { name: 'Kwik Bike', icon: Bike, base: 800, perKm: 150, time: '25 min', color: 'indigo' },
    { name: 'GIG Express', icon: Truck, base: 2500, perKm: 300, time: '45 min', color: 'purple' },
    { name: 'Red Star Van', icon: Truck, base: 4500, perKm: 500, time: '1 hr', color: 'blue' },
  ];

  const handleCalculate = () => {
    if (!pickup || !dropoff) return;
    setStep('calculating');
    setTimeout(() => setStep('results'), 1800);
  };

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setStep('input');
      setPickup('');
      setDropoff('');
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          key="calculator-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-[#0a0a14] border border-white/10 rounded-[32px] overflow-hidden shadow-2xl glass-card p-8"
          >
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content Logic */}
            <div className="mt-4">
              {step === 'input' && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="space-y-1">
                    <h2 className="text-2xl font-bold">Price Calculator</h2>
                    <p className="text-gray-500 text-sm">Get real-time quotes across Lagos State.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-400" />
                      <input 
                        type="text" 
                        placeholder="Pickup (e.g., Ikeja City Mall)" 
                        value={pickup}
                        onChange={(e) => setPickup(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm"
                      />
                    </div>
                    <div className="flex justify-center -my-2">
                       <div className="w-px h-6 bg-indigo-500/30" />
                    </div>
                    <div className="relative">
                      <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                      <input 
                        type="text" 
                        placeholder="Drop-off (e.g., Victoria Island)" 
                        value={dropoff}
                        onChange={(e) => setDropoff(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm"
                      />
                    </div>
                  </div>

                  <button 
                    onClick={handleCalculate}
                    disabled={!pickup || !dropoff}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-50 py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 group active:scale-95 transition-all"
                  >
                    <span>Get Free Estimate</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              )}

              {step === 'calculating' && (
                <div className="py-12 flex flex-col items-center justify-center space-y-8">
                  <div className="relative">
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute inset-0 bg-indigo-500 rounded-full blur-2xl"
                    />
                    <div className="relative w-24 h-24 rounded-full border-2 border-indigo-500/30 flex items-center justify-center">
                      <Navigation className="w-10 h-10 text-indigo-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold">Synchronizing Logistics...</h3>
                    <p className="text-gray-500 text-sm mt-2">Connecting to local providers API</p>
                  </div>
                </div>
              )}

              {step === 'results' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                      <Activity className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold italic">Top Estimates</h3>
                      <p className="text-xs text-gray-500">Route: {pickup} → {dropoff}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {providers.map((p, i) => (
                      <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0, transition: { delay: i * 0.1 } }}
                        key={p.name}
                        className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-indigo-500/30 transition-all cursor-pointer group"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`p-2 rounded-lg bg-${p.color}-500/10 text-${p.color}-400`}>
                            <p.icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">{p.name}</p>
                            <p className="text-[10px] text-gray-500">ETA: {p.time}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-400">₦{(p.base + 12 * p.perKm).toLocaleString()}</p>
                          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-tighter">Est. Total</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <button 
                    onClick={() => setStep('input')}
                    className="w-full py-4 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Adjust Route
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PriceCalculator;
